/**
 * Attachments — File attachment storage and resolution for chat transcripts
 *
 * Handles saving user-attached files to the run folder,
 * materializing generated images into the session folder, and
 * reading them back as base64 for API requests.
 */
import type { Workspace } from '../types'
import { decodeDataUrl, materializeGeneratedImageDataUrl } from '@secureblob/sdk'
import type { Attachment } from '../agentTypes'
import { ensureAgentSessionDirectories } from './sessionPaths'

// ────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 MB
const MAX_ATTACHMENTS = 10

// ────────────────────────────────────────────────────────────────
// Save attachments to transcript session folder
// ────────────────────────────────────────────────────────────────

/**
 * Save File objects to the transcript session folder in the workspace.
 * Returns Attachment metadata for each saved file.
 *
 * Files are stored at: <agentPath>.transcripts/<sessionId>/<filename>
 */
export async function saveAttachmentsToRunFolder(
    files: File[],
    workspace: Workspace,
    agentPath: string,
    sessionId: string,
): Promise<Attachment[]> {
    const attachments: Attachment[] = []
    const limited = files.slice(0, MAX_ATTACHMENTS)
    const sessionPaths = ensureAgentSessionDirectories(workspace, agentPath, sessionId)
    await Promise.resolve(workspace.fs.mkdir?.(sessionPaths.transcriptsPath))
    await Promise.resolve(workspace.fs.mkdir?.(sessionPaths.sessionFolderPath))

    for (const file of limited) {
        if (file.size > MAX_FILE_SIZE) {
            console.warn(`[Attachments] Skipping ${file.name}: exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`)
            continue
        }

        const safeName = sanitizeFilename(file.name)
        const sessionDir = sessionPaths.sessionFolderPath
        const filePath = `${sessionDir}/${safeName}`

        try {
            const arrayBuffer = await file.arrayBuffer()
            const bytes = new Uint8Array(arrayBuffer)
            await Promise.resolve(workspace.fs.writeBytes(filePath, bytes))

            attachments.push({
                path: filePath,
                mime: file.type || guessMimeFromName(file.name),
                size: file.size,
                name: file.name,
            })
        } catch (err) {
            console.error(`[Attachments] Failed to save ${file.name}:`, err)
        }
    }

    return attachments
}

/**
 * Read an attachment back as a base64 data URL.
 * Returns null if the file cannot be read.
 */
export async function readAttachmentAsBase64(
    attachment: Attachment,
    workspace: Workspace,
): Promise<string | null> {
    try {
        const bytes = await workspace.fs.readBinary(attachment.path)
        if (!bytes) return null
        const base64 = bytesToBase64(bytes)
        return `data:${attachment.mime};base64,${base64}`
    } catch {
        return null
    }
}

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

/** Sanitize filename: remove path separators, limit length */
function sanitizeFilename(name: string): string {
    return name
        .replace(/[/\\]/g, '_')
        .replace(/[^\w.-]/g, '_')
        .slice(0, 200)
}

/** Guess MIME type from filename extension */
function guessMimeFromName(name: string): string {
    const ext = name.substring(name.lastIndexOf('.')).toLowerCase()
    const mimeMap: Record<string, string> = {
        '.png': 'image/png',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.gif': 'image/gif',
        '.webp': 'image/webp',
        '.pdf': 'application/pdf',
        '.txt': 'text/plain',
        '.md': 'text/markdown',
        '.json': 'application/json',
        '.csv': 'text/csv',
    }
    return mimeMap[ext] ?? 'application/octet-stream'
}

/** Convert Uint8Array to base64 string */
function bytesToBase64(bytes: Uint8Array): string {
    let binary = ''
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
}

// ────────────────────────────────────────────────────────────────
// Generated image persistence
// ────────────────────────────────────────────────────────────────

/**
 * Materialize a generated image (data-URL) into the transcript session folder.
 *
 * Returns the workspace path of the saved file, or null if the data URL
 * cannot be decoded or the write fails. The caller should update
 * `assistant_image.savedPath` with the returned path on success.
 *
 * File naming: `gen-{counter}-{timestamp}.{ext}`
 * - counter is per-session, monotonically increasing, so files never collide
 * - extension is inferred from MIME type, defaulting to `.png`
 */
export async function saveGeneratedImage(
    dataUrl: string,
    workspace: Workspace,
    agentPath: string,
    sessionId: string,
    counter: number,
): Promise<string | null> {
    const sessionPaths = ensureAgentSessionDirectories(workspace, agentPath, sessionId)
    await Promise.resolve(workspace.fs.mkdir?.(sessionPaths.transcriptsPath))
    await Promise.resolve(workspace.fs.mkdir?.(sessionPaths.sessionFolderPath))
    try {
        return await materializeGeneratedImageDataUrl(dataUrl, {
            sessionDir: sessionPaths.sessionFolderPath,
            counter,
            writeBytes: (path, bytes) => workspace.fs.writeBytes(path, bytes),
        })
    } catch (err) {
        console.error('[Attachments] Failed to materialize generated image:', err)
        return null
    }
}
export { decodeDataUrl }

// ────────────────────────────────────────────────────────────────
// Artifact promotion (session → visible workspace)
// ────────────────────────────────────────────────────────────────

/**
 * Promote a generated image from the hidden session folder to a visible
 * workspace path next to the agent file.
 *
 * The session-folder original is left untouched. The copy uses the same
 * collision pattern as FileBrowser ("copy", "copy 2", …).
 *
 * Returns the promoted workspace path, or null if the source cannot be read
 * or the promoted copy cannot be written.
 */
export async function promoteGeneratedImage(
    savedPath: string,
    workspace: Workspace,
    agentPath: string,
): Promise<string | null> {
    let bytes: Uint8Array | null
    try {
        bytes = await workspace.fs.readBinary(savedPath)
    } catch (err) {
        console.error('[Attachments] Failed to read generated image for promotion:', err)
        return null
    }
    if (!bytes) return null

    // Determine destination parent: directory containing the .agent.json
    const agentParts = agentPath.split('/')
    agentParts.pop()
    const parentDir = agentParts.join('/') || '/'

    // Extract base filename from session path
    const sourceName = savedPath.split('/').pop() ?? 'generated-image.png'

    // Collision handling: "name.ext" → "name copy.ext" → "name copy 2.ext" …
    const [base, ext] = sourceName.includes('.')
        ? [sourceName.substring(0, sourceName.lastIndexOf('.')), sourceName.substring(sourceName.lastIndexOf('.'))]
        : [sourceName, '']

    let existingFiles
    try {
        existingFiles = await workspace.fs.listDirAsync(parentDir)
    } catch (err) {
        console.error('[Attachments] Failed to inspect destination for generated image promotion:', err)
        return null
    }
    const existingNames = new Set(existingFiles.map(f => {
        const parts = f.path.split('/')
        return parts[parts.length - 1]
    }))

    let newName = sourceName
    let copyNumber = 0
    while (existingNames.has(newName)) {
        copyNumber++
        newName = copyNumber === 1 ? `${base} copy${ext}` : `${base} copy ${copyNumber}${ext}`
    }

    const promotedPath = parentDir === '/' ? `/${newName}` : `${parentDir}/${newName}`
    try {
        await Promise.resolve(workspace.fs.writeBytes(promotedPath, bytes))
        return promotedPath
    } catch (err) {
        console.error('[Attachments] Failed to promote generated image:', err)
        return null
    }
}
